import React, { useMemo } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts';
import { format } from 'date-fns';
import { useServerInfo, usePlayers, useSettings, useMetrics, useMetricsHistory, usePlayersOnlineHistory, usePlayerSessions, useUptimeStatus, buildExportUrl } from '../hooks/useApi';
import type { ServerInfo, Player, ServerSettings, MetricsRecord, PlayersOnlineRecord, PlayerSession, UptimeStatus } from '../types';
import { PlayerMap } from './PlayerMap';
import './Dashboard.css';

function formatUptime(seconds: number): string {
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  
  if (days > 0) {
    return `${days}d ${hours}h ${minutes}m ${secs}s`;
  } else if (hours > 0) {
    return `${hours}h ${minutes}m ${secs}s`;
  } else if (minutes > 0) {
    return `${minutes}m ${secs}s`;
  }
  return `${secs}s`;
}

interface MetricCardProps {
  title: string;
  value: string | number;
  unit?: string;
  icon?: React.ReactNode;
  color?: string;
}

function MetricCard({ title, value, unit, icon, color }: MetricCardProps) {
  return (
    <div className="metric-card">
      <div className="metric-header">
        <span className="metric-title">{title}</span>
        {icon && <span className="metric-icon">{icon}</span>}
      </div>
      <div className="metric-value" style={{ color }}>
        {value}
        {unit && <span className="metric-unit">{unit}</span>}
      </div>
    </div>
  );
}

function ServerStatusCard({ info }: { info: ServerInfo | null }) {
  if (!info) return <div className="card loading">Loading server info...</div>;

  return (
    <div className="card server-status">
      <h3>Server Information</h3>
      <div className="server-info-grid">
        <div className="info-item">
          <span className="info-label">Server Name</span>
          <span className="info-value">{info.servername}</span>
        </div>
        <div className="info-item">
          <span className="info-label">Version</span>
          <span className="info-value">{info.version}</span>
        </div>
        <div className="info-item">
          <span className="info-label">World GUID</span>
          <span className="info-value worldguid" title={info.worldguid}>
            {info.worldguid.length > 16 ? info.worldguid.slice(0, 16) + '...' : info.worldguid}
          </span>
        </div>
        <div className="info-item">
          <span className="info-label">Description</span>
          <span className="info-value">{info.description || 'No description'}</span>
        </div>
      </div>
    </div>
  );
}

function PlayersList({ players, maxPlayers }: { players: Player[]; maxPlayers?: number }) {
  return (
    <div className="card players-list">
      <div className="card-header">
        <h3>Online Players ({players.length}/{maxPlayers || '?'})</h3>
      </div>
      {players.length === 0 ? (
        <div className="empty-state">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
            <circle cx="9" cy="7" r="4" />
            <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
            <path d="M16 3.13a4 4 0 0 1 0 7.75" />
          </svg>
          <p>No players currently online</p>
        </div>
      ) : (
        <div className="players-table-container">
          <table className="players-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Account</th>
                <th>Level</th>
                <th>Ping</th>
                <th>Location</th>
                <th>Player ID</th>
                <th>User ID</th>
              </tr>
            </thead>
            <tbody>
              {players.map((player) => (
                <tr key={player.playerId}>
                  <td className="player-name">{player.name}</td>
                  <td>{player.accountName}</td>
                  <td><span className="level-badge">Lv.{player.level}</span></td>
                  <td>{player.ping.toFixed(1)} ms</td>
                  <td className="code">({player.location_x.toFixed(0)}, {player.location_y.toFixed(0)})</td>
                  <td className="code">{player.playerId}</td>
                  <td className="code">{player.userId}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function PlayersChart({ history }: { history: PlayersOnlineRecord[] | undefined }) {
  if (!history?.length) return null;

  // Downsample data to max 200 points for smooth rendering
  const downsample = <T,>(arr: T[], maxPoints: number): T[] => {
    if (arr.length <= maxPoints) return arr;
    const step = Math.ceil(arr.length / maxPoints);
    return arr.filter((_, i) => i % step === 0);
  };

  const data = history
    .slice()
    .reverse()
    .map(record => ({
      time: format(new Date(record.timestamp), 'HH:mm:ss'),
      players: record.playerCount,
      maxPlayers: record.maxPlayers,
    }))
    .filter(d => 
      d.time &&
      typeof d.players === 'number' && !isNaN(d.players) && isFinite(d.players) &&
      d.players >= 0 && d.players <= 1000 &&
      typeof d.maxPlayers === 'number' && !isNaN(d.maxPlayers) && isFinite(d.maxPlayers) &&
      d.maxPlayers >= 0 && d.maxPlayers <= 1000
    );

  const chartData = downsample(data, 200);

  if (!chartData.length) return null;

  return (
    <div className="card chart-card">
      <h3>Players Online (Last 24h)</h3>
      <div className="chart-container">
        <ResponsiveContainer width="100%" height={280}>
          <AreaChart data={chartData}>
            <defs>
              <linearGradient id="playersGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
            <XAxis dataKey="time" stroke="#9ca3af" fontSize={12} tick={{ fill: '#9ca3af' }} />
            <YAxis stroke="#9ca3af" fontSize={12} tick={{ fill: '#9ca3af' }} />
            <Tooltip 
              contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px', color: '#f9fafb' }}
              labelFormatter={(time) => `Time: ${time}`}
            />
            <Area type="monotone" dataKey="players" stroke="#3b82f6" fillOpacity={1} fill="url(#playersGradient)" />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function ServerMetricsChart({ history }: { history: MetricsRecord[] | undefined }) {
  if (!history?.length) return null;

  // Downsample data to max 200 points for smooth rendering
  const downsample = <T,>(arr: T[], maxPoints: number): T[] => {
    if (arr.length <= maxPoints) return arr;
    const step = Math.ceil(arr.length / maxPoints);
    return arr.filter((_, i) => i % step === 0);
  };

  const data = history
    .slice()
    .reverse()
    .map(record => ({
      time: format(new Date(record.timestamp), 'HH:mm:ss'),
      fps: record.serverFps,
      frameTime: record.serverFrameTime,
    }))
    .filter(d => 
      d.time &&
      typeof d.fps === 'number' && !isNaN(d.fps) && isFinite(d.fps) &&
      d.fps >= 0 && d.fps <= 200 &&
      typeof d.frameTime === 'number' && !isNaN(d.frameTime) && isFinite(d.frameTime) &&
      d.frameTime >= 0 && d.frameTime <= 1000
    );

  const chartData = downsample(data, 200);

  if (!chartData.length) return null;

  return (
    <div className="card chart-card">
      <h3>Server Performance (Last 24h)</h3>
      <div className="chart-container">
        <ResponsiveContainer width="100%" height={280}>
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
            <XAxis dataKey="time" stroke="#9ca3af" fontSize={12} tick={{ fill: '#9ca3af' }} />
            <YAxis 
              yAxisId="left"
              stroke="#9ca3af" 
              fontSize={12} 
              tick={{ fill: '#9ca3af' }}
              domain={[0, 120]}
            />
            <YAxis 
              yAxisId="right"
              orientation="right"
              stroke="#9ca3af" 
              fontSize={12} 
              tick={{ fill: '#9ca3af' }}
              domain={[0, 'dataMax']}
            />
            <Tooltip 
              contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px', color: '#f9fafb' }}
              labelFormatter={(time) => `Time: ${time}`}
            />
            <Line 
              type="monotone" 
              dataKey="fps" 
              stroke="#22c55e" 
              strokeWidth={2} 
              dot={false}
              yAxisId="left"
              name="Server FPS"
            />
            <Line 
              type="monotone" 
              dataKey="frameTime" 
              stroke="#f59e0b" 
              strokeWidth={2} 
              dot={false}
              yAxisId="right"
              name="Frame Time (ms)"
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div className="chart-legend">
        <span className="legend-item"><span className="legend-color" style={{ backgroundColor: '#22c55e' }} /> Server FPS</span>
        <span className="legend-item"><span className="legend-color" style={{ backgroundColor: '#f59e0b' }} /> Frame Time (ms)</span>
      </div>
    </div>
  );
}

function WorldStatsChart({ history }: { history: MetricsRecord[] | undefined }) {
  if (!history?.length) return null;

  // Downsample data to max 200 points for smooth rendering
  const downsample = <T,>(arr: T[], maxPoints: number): T[] => {
    if (arr.length <= maxPoints) return arr;
    const step = Math.ceil(arr.length / maxPoints);
    return arr.filter((_, i) => i % step === 0);
  };

  const data = history
    .slice()
    .reverse()
    .map(record => ({
      time: format(new Date(record.timestamp), 'HH:mm:ss'),
      baseCamps: record.baseCampNum,
      days: record.days,
    }))
    .filter(d => 
      d.time &&
      typeof d.baseCamps === 'number' && !isNaN(d.baseCamps) && isFinite(d.baseCamps) &&
      d.baseCamps >= 0 && d.baseCamps <= 1000 &&
      typeof d.days === 'number' && !isNaN(d.days) && isFinite(d.days) &&
      d.days >= 0 && d.days <= 10000
    );

  const chartData = downsample(data, 200);

  if (!chartData.length) return null;

  return (
    <div className="card chart-card">
      <h3>World Statistics (Last 24h)</h3>
      <div className="chart-container">
        <ResponsiveContainer width="100%" height={280}>
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
            <XAxis dataKey="time" stroke="#9ca3af" fontSize={12} tick={{ fill: '#9ca3af' }} />
            <YAxis 
              yAxisId="left"
              stroke="#9ca3af" 
              fontSize={12} 
              tick={{ fill: '#9ca3af' }}
              domain={[0, 'dataMax']}
            />
            <YAxis 
              yAxisId="right"
              orientation="right"
              stroke="#9ca3af" 
              fontSize={12} 
              tick={{ fill: '#9ca3af' }}
              domain={[0, 'dataMax']}
            />
            <Tooltip 
              contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px', color: '#f9fafb' }}
              labelFormatter={(time) => `Time: ${time}`}
            />
            <Line 
              type="monotone" 
              dataKey="baseCamps" 
              stroke="#3b82f6" 
              strokeWidth={2} 
              dot={false}
              yAxisId="left"
              name="Base Camps"
            />
            <Line 
              type="monotone" 
              dataKey="days" 
              stroke="#a855f7" 
              strokeWidth={2} 
              dot={false}
              yAxisId="right"
              name="In-Game Days"
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div className="chart-legend">
        <span className="legend-item"><span className="legend-color" style={{ backgroundColor: '#3b82f6' }} /> Base Camps</span>
        <span className="legend-item"><span className="legend-color" style={{ backgroundColor: '#a855f7' }} /> In-Game Days</span>
      </div>
    </div>
  );
}

function SettingsPanel({ settings }: { settings: ServerSettings | null }) {
  if (!settings) return <div className="card loading">Loading settings...</div>;

  return (
    <div className="card settings-panel">
      <h3>Server Settings</h3>
      <div className="settings-grid">
        <div className="setting-group">
          <h4>Gameplay</h4>
          <div className="setting-row">
            <span>Difficulty</span>
            <span className="setting-value">{settings.Difficulty}</span>
          </div>
          <div className="setting-row">
            <span>Day Speed</span>
            <span className="setting-value">{settings.DayTimeSpeedRate}x</span>
          </div>
          <div className="setting-row">
            <span>Night Speed</span>
            <span className="setting-value">{settings.NightTimeSpeedRate}x</span>
          </div>
          <div className="setting-row">
            <span>EXP Rate</span>
            <span className="setting-value">{settings.ExpRate}x</span>
          </div>
          <div className="setting-row">
            <span>Pal Capture Rate</span>
            <span className="setting-value">{settings.PalCaptureRate}x</span>
          </div>
          <div className="setting-row">
            <span>Pal Spawn Rate</span>
            <span className="setting-value">{settings.PalSpawnNumRate}x</span>
          </div>
          <div className="setting-row">
            <span>Work Speed</span>
            <span className="setting-value">{settings.WorkSpeedRate}x</span>
          </div>
          <div className="setting-row">
            <span>Death Penalty</span>
            <span className="setting-value">{settings.DeathPenalty}</span>
          </div>
        </div>

        <div className="setting-group">
          <h4>Server</h4>
          <div className="setting-row">
            <span>Max Players</span>
            <span className="setting-value">{settings.ServerPlayerMaxNum}</span>
          </div>
          <div className="setting-row">
            <span>PvP Enabled</span>
            <span className="setting-value">{settings.bIsPvP ? 'Yes' : 'No'}</span>
          </div>
          <div className="setting-row">
            <span>Multiplayer</span>
            <span className="setting-value">{settings.bIsMultiplay ? 'Yes' : 'No'}</span>
          </div>
          <div className="setting-row">
            <span>Fast Travel</span>
            <span className="setting-value">{settings.bEnableFastTravel ? 'Yes' : 'No'}</span>
          </div>
          <div className="setting-row">
            <span>Friendly Fire</span>
            <span className="setting-value">{settings.bEnableFriendlyFire ? 'Yes' : 'No'}</span>
          </div>
          <div className="setting-row">
            <span>Coop Max Players</span>
            <span className="setting-value">{settings.CoopPlayerMaxNum}</span>
          </div>
        </div>

        <div className="setting-group">
          <h4>Rates</h4>
          <div className="setting-row">
            <span>Damage Rate (Player)</span>
            <span className="setting-value">{settings.PlayerDamageRateAttack}x / {settings.PlayerDamageRateDefense}x</span>
          </div>
          <div className="setting-row">
            <span>Damage Rate (Pal)</span>
            <span className="setting-value">{settings.PalDamageRateAttack}x / {settings.PalDamageRateDefense}x</span>
          </div>
          <div className="setting-row">
            <span>Stomach Decrease (Player)</span>
            <span className="setting-value">{settings.PlayerStomachDecreaceRate}x</span>
          </div>
          <div className="setting-row">
            <span>Stamina Decrease (Player)</span>
            <span className="setting-value">{settings.PlayerStaminaDecreaceRate}x</span>
          </div>
          <div className="setting-row">
            <span>Auto HP Regen (Player)</span>
            <span className="setting-value">{settings.PlayerAutoHPRegeneRate}x</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function UptimeCard({ uptime }: { uptime: UptimeStatus | null }) {
  if (!uptime) return null;

  const online = uptime.status === 'online';
  const availability = uptime.availabilityPercent;
  const availColor = availability >= 99.5 ? '#22c55e' : availability >= 95 ? '#f59e0b' : '#ef4444';
  const outages = uptime.outages || [];

  return (
    <div className="card">
      <div className="card-header">
        <h3>Uptime &amp; Availability (Last 24h)</h3>
        <div className={`status-indicator ${online ? 'online' : 'offline'}`}>
          <span className="status-dot" />
          <span>{online ? 'Online' : 'Offline'}</span>
        </div>
      </div>

      <div className="metrics-grid">
        <MetricCard title="Availability" value={availability.toFixed(2)} unit="%" icon="📶" color={availColor} />
        <MetricCard title="Total Downtime" value={formatUptime(uptime.downtimeSeconds)} icon="🛑" color={uptime.downtimeSeconds > 0 ? '#ef4444' : '#22c55e'} />
        <MetricCard title="Outages" value={uptime.outageCount} icon="⚠️" color={uptime.outageCount > 0 ? '#f59e0b' : '#22c55e'} />
      </div>

      {outages.length > 0 && (
        <div className="sessions-table-container">
          <table className="sessions-table">
            <thead>
              <tr>
                <th>Started</th>
                <th>Ended</th>
                <th>Duration</th>
                <th>Reason</th>
              </tr>
            </thead>
            <tbody>
              {outages.slice(0, 10).map((o) => {
                const start = new Date(o.startTime);
                const end = o.endTime ? new Date(o.endTime) : null;
                const durationSec = Math.max(0, Math.round(((end ? end.getTime() : Date.now()) - start.getTime()) / 1000));
                return (
                  <tr key={o.id}>
                    <td>{format(start, 'MMM d, HH:mm:ss')}</td>
                    <td>{end ? format(end, 'MMM d, HH:mm:ss') : <span style={{ color: '#ef4444' }}>Ongoing</span>}</td>
                    <td><strong>{formatUptime(durationSec)}</strong></td>
                    <td className="code" title={o.error}>{o.error ? (o.error.length > 60 ? o.error.slice(0, 60) + '…' : o.error) : '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function Dashboard() {
  const { data: serverInfo, loading: infoLoading } = useServerInfo();
  const { data: players } = usePlayers();
  const { data: settings } = useSettings();
  const { data: metrics } = useMetrics();
  
  // Request last 24 hours of data explicitly - memoized to prevent re-fetches
  const startTime = useMemo(() => new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(), []);
  
  const { data: metricsHistory } = useMetricsHistory({ limit: 2880, start_time: startTime });
  const { data: playersHistory } = usePlayersOnlineHistory({ limit: 2880, start_time: startTime });
  const { data: playerSessions, loading: sessionsLoading } = usePlayerSessions({ limit: 5000, start_time: startTime });
  const { data: uptimeStatus } = useUptimeStatus({ start_time: startTime });

  const currentPlayers = players?.length || 0;
  const maxPlayers = settings?.ServerPlayerMaxNum || 32;
  const serverFps = metrics?.serverfps || 0;
  const serverFpsAvg = metrics?.serverfpsaverage || 0;
  const frameTime = metrics?.serverframetime || 0;
  const baseCamps = metrics?.basecampnum || 0;
  const inGameDays = metrics?.days || 0;
  const uptime = formatUptime(metrics?.uptime || 0);

  return (
    <div className="dashboard">
      <header className="dashboard-header">
        <div className="header-left">
          <h1>Palworld Server Status</h1>
          <span className="server-badge">{infoLoading ? 'Connecting...' : 'Live'}</span>
        </div>
        <div className="header-right">
          {(() => {
            const isOffline = uptimeStatus ? uptimeStatus.status === 'offline' : infoLoading;
            const label = infoLoading && !uptimeStatus
              ? 'Connecting...'
              : isOffline ? 'Server Offline' : 'Server Online';
            return (
              <div className={`status-indicator ${isOffline ? 'offline' : 'online'}`}>
                <span className="status-dot" />
                <span>{label}</span>
              </div>
            );
          })()}
        </div>
      </header>

      <div className="metrics-grid">
        <MetricCard title="Players Online" value={currentPlayers} unit={` / ${maxPlayers}`} icon="👥" color="#3b82f6" />
        <MetricCard title="Server FPS" value={serverFps} unit={` (avg ${serverFpsAvg.toFixed(1)})`} icon="⚡" color={serverFps >= 55 ? '#22c55e' : serverFps >= 30 ? '#f59e0b' : '#ef4444'} />
        <MetricCard title="Frame Time" value={frameTime.toFixed(1)} unit=" ms" icon="⏱️" color={frameTime <= 18 ? '#22c55e' : frameTime <= 33 ? '#f59e0b' : '#ef4444'} />
        <MetricCard title="Uptime" value={uptime} icon="🕐" color="#8b5cf6" />
      </div>

      <div className="metrics-grid">
        <MetricCard title="In-Game Days" value={inGameDays} icon="🌅" color="#f59e0b" />
        <MetricCard title="Base Camps" value={baseCamps} icon="🏕️" color="#3b82f6" />
      </div>

      <UptimeCard uptime={uptimeStatus} />

      <div className="dashboard-grid">
        <div className="main-column">
          <ServerStatusCard info={serverInfo} />
          <PlayersList players={players || []} maxPlayers={maxPlayers} />
          
          {/* Player Location Map */}
          <PlayerMap players={players || []} mapImageUrl="/palworld-map-tile.png" />
        </div>

        <div className="side-column">
          <PlayersChart history={playersHistory} />
          <ServerMetricsChart history={metricsHistory} />
          <WorldStatsChart history={metricsHistory} />
        </div>
      </div>

      <SettingsPanel settings={settings} />

      {/* Player Sessions Panel */}
      <PlayerSessionsPanel sessions={playerSessions || []} loading={sessionsLoading} />

      {/* Export Buttons */}
      <ExportButtons 
        startTime={startTime}
      />
    </div>
  );
}

// Player Sessions Panel
function PlayerSessionsPanel({ sessions, loading }: { sessions: PlayerSession[]; loading: boolean }) {
  if (!sessions.length && !loading) return null;

  return (
    <div className="card">
      <div className="card-header">
        <h3>Player Sessions (Last 24h)</h3>
        {loading && <span className="loading-badge">Loading...</span>}
      </div>
      {sessions.length === 0 ? (
        <div className="empty-state">No play sessions recorded in the last 24 hours</div>
      ) : (
        <div className="sessions-table-container">
          <table className="sessions-table">
            <thead>
              <tr>
                <th>Player</th>
                <th>Steam ID</th>
                <th>Join Time</th>
                <th>Leave Time</th>
                <th>Duration</th>
                <th>Snapshots</th>
                <th>Max Level</th>
              </tr>
            </thead>
            <tbody>
              {sessions.slice(0, 20).map((session, index) => (
                <tr key={`${session.player_id}-${session.join_time}-${index}`}>
                  <td>{session.name}</td>
                  <td className="code">{session.user_id}</td>
                  <td>{format(new Date(session.join_time), 'MMM d, HH:mm:ss')}</td>
                  <td>{format(new Date(session.leave_time), 'MMM d, HH:mm:ss')}</td>
                  <td><strong>{session.duration_human}</strong></td>
                  <td>{session.snapshots}</td>
                  <td>{session.max_level}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// Export Buttons Component
function ExportButtons({ 
  startTime 
}: { 
  startTime: string; 
}) {
  const handleExport = (url: string, filename: string) => {
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <div className="card export-card">
      <div className="card-header">
        <h3>Export Data</h3>
      </div>
      <div className="export-buttons">
        <button 
          className="export-btn"
          onClick={() => handleExport(
            buildExportUrl('/export/sessions', { start_time: startTime, limit: '5000' }),
            `sessions_${new Date().toISOString().split('T')[0]}.csv`
          )}
        >
          📊 Download Sessions CSV
        </button>
        <button 
          className="export-btn"
          onClick={() => handleExport(
            buildExportUrl('/export/players', { start_time: startTime, limit: '10000' }),
            `players_${new Date().toISOString().split('T')[0]}.csv`
          )}
        >
          👥 Download Player Snapshots CSV
        </button>
        <button 
          className="export-btn"
          onClick={() => handleExport(
            buildExportUrl('/export/metrics', { start_time: startTime }),
            `metrics_${new Date().toISOString().split('T')[0]}.csv`
          )}
        >
          📈 Download Metrics CSV
        </button>
      </div>
    </div>
  );
}